import React, { useState, useEffect } from 'react';
import { 
    TrendingUp, Users, Calendar, Award, Clock, Target, 
    BarChart3, PieChart, Activity, Download, Filter
} from 'lucide-react';

const AnalyticsDashboard = () => {
    const [timeframe, setTimeframe] = useState('30d');
    const [metricsData, setMetricsData] = useState({
        personalMetrics: {
            timeToFill: 18.5,
            interviewsScheduled: 45,
            hiresMade: 12,
            conversionRate: 26.7
        },
        requisitionHealth: {
            totalRequisitions: 12,
            agingJobs: 3,
            pipelineDropOffs: 15,
            averageAge: 22
        },
        sourceEffectiveness: [
            { source: 'LinkedIn', applicants: 142, hires: 8, conversion: 5.6 },
            { source: 'Indeed', applicants: 89, hires: 3, conversion: 3.4 },
            { source: 'Referrals', applicants: 34, hires: 6, conversion: 17.6 },
            { source: 'Company Website', applicants: 67, hires: 2, conversion: 3.0 }
        ],
        pipelineMetrics: {
            totalCandidates: 332,
            stageDistribution: [
                { stage: 'Applied', count: 145, percentage: 43.7 },
                { stage: 'Screening', count: 89, percentage: 26.8 },
                { stage: 'Interview', count: 67, percentage: 20.2 },
                { stage: 'Offer', count: 19, percentage: 5.7 },
                { stage: 'Hired', count: 12, percentage: 3.6 }
            ]
        }
    });

    const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-600 font-medium">{title}</p>
                    <div className="flex items-baseline mt-2">
                        <p className="text-2xl font-bold text-gray-900">{value}</p>
                        {subtitle && <p className="ml-2 text-sm text-gray-500">{subtitle}</p>}
                    </div>
                    {trend && (
                        <div className={`flex items-center mt-1 text-sm ${
                            trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {trend.value} from last month
                        </div>
                    )}
                </div>
                <div className={`p-2 bg-${color}-100 rounded-lg`}>
                    <Icon className={`h-6 w-6 text-${color}-600`} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Analytics Header */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Analytics Dashboard</h3>
                        <p className="text-sm text-gray-600">Track your recruiting performance and pipeline health</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <select 
                            value={timeframe}
                            onChange={(e) => setTimeframe(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                            <option value="1y">Last year</option>
                        </select>
                        <button 
                            onClick={() => {
                                // Simulate export functionality
                                const csvContent = "data:text/csv;charset=utf-8,Metric,Value\nTime to Fill,18.5 days\nInterviews Scheduled,45\nHires Made,12\nConversion Rate,26.7%";
                                const encodedUri = encodeURI(csvContent);
                                const link = document.createElement("a");
                                link.setAttribute("href", encodedUri);
                                link.setAttribute("download", `recruiter_analytics_${new Date().toISOString().split('T')[0]}.csv`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export
                        </button>
                    </div>
                </div>
            </div>

            {/* Personal Metrics */}
            <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Personal Performance</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <MetricCard 
                        title="Average Time to Fill"
                        value={`${metricsData.personalMetrics.timeToFill}`}
                        subtitle="days"
                        icon={Clock}
                        trend={{ direction: 'up', value: '+2.1 days' }}
                        color="blue"
                    />
                    <MetricCard 
                        title="Interviews Scheduled"
                        value={metricsData.personalMetrics.interviewsScheduled}
                        icon={Calendar}
                        trend={{ direction: 'up', value: '+12%' }}
                        color="green"
                    />
                    <MetricCard 
                        title="Hires Made"
                        value={metricsData.personalMetrics.hiresMade}
                        icon={Award}
                        trend={{ direction: 'up', value: '+4 hires' }}
                        color="purple"
                    />
                    <MetricCard 
                        title="Conversion Rate"
                        value={`${metricsData.personalMetrics.conversionRate}%`}
                        icon={Target}
                        trend={{ direction: 'up', value: '+3.2%' }}
                        color="yellow"
                    />
                </div>
            </div>

            {/* Pipeline Health & Source Effectiveness */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Requisition Health */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Requisition Health</h4>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Total Active Requisitions</span>
                            <span className="text-lg font-semibold text-gray-900">{metricsData.requisitionHealth.totalRequisitions}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Aging Jobs (30+ days)</span>
                            <span className="text-lg font-semibold text-red-600">{metricsData.requisitionHealth.agingJobs}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Pipeline Drop-offs</span>
                            <span className="text-lg font-semibold text-yellow-600">{metricsData.requisitionHealth.pipelineDropOffs}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Average Requisition Age</span>
                            <span className="text-lg font-semibold text-gray-900">{metricsData.requisitionHealth.averageAge} days</span>
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center text-sm text-gray-600">
                            <Activity className="h-4 w-4 mr-2" />
                            <span>Pipeline health score: </span>
                            <span className="ml-1 font-semibold text-green-600">Good (82/100)</span>
                        </div>
                    </div>
                </div>

                {/* Source Effectiveness */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Source Effectiveness</h4>
                    <div className="space-y-3">
                        {metricsData.sourceEffectiveness.map((source, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">{source.source}</span>
                                        <span className="text-sm font-semibold text-blue-600">{source.conversion}%</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                                        <span>{source.applicants} applicants</span>
                                        <span>{source.hires} hires</span>
                                    </div>
                                    <div className="mt-2">
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full" 
                                                style={{ width: `${Math.min(source.conversion * 5, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Pipeline Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Pipeline Distribution</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Pipeline Chart */}
                    <div>
                        <div className="space-y-4">
                            {metricsData.pipelineMetrics.stageDistribution.map((stage, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                                        <div className="text-right">
                                            <span className="text-sm font-semibold text-gray-900">{stage.count}</span>
                                            <span className="text-xs text-gray-500 ml-1">({stage.percentage}%)</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div 
                                            className={`h-3 rounded-full ${
                                                index === 0 ? 'bg-blue-500' :
                                                index === 1 ? 'bg-yellow-500' :
                                                index === 2 ? 'bg-purple-500' :
                                                index === 3 ? 'bg-green-500' :
                                                'bg-emerald-500'
                                            }`}
                                            style={{ width: `${stage.percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pipeline Insights */}
                    <div>
                        <h5 className="text-sm font-semibold text-gray-900 mb-3">Pipeline Insights</h5>
                        <div className="space-y-3">
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-start">
                                    <div className="p-1 bg-blue-100 rounded">
                                        <TrendingUp className="h-3 w-3 text-blue-600" />
                                    </div>
                                    <div className="ml-2">
                                        <p className="text-xs font-medium text-blue-900">Strong Application Volume</p>
                                        <p className="text-xs text-blue-700 mt-1">145 new applications this month (+23% from last month)</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 bg-yellow-50 rounded-lg">
                                <div className="flex items-start">
                                    <div className="p-1 bg-yellow-100 rounded">
                                        <Clock className="h-3 w-3 text-yellow-600" />
                                    </div>
                                    <div className="ml-2">
                                        <p className="text-xs font-medium text-yellow-900">Screening Bottleneck</p>
                                        <p className="text-xs text-yellow-700 mt-1">89 candidates awaiting screening review</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 bg-green-50 rounded-lg">
                                <div className="flex items-start">
                                    <div className="p-1 bg-green-100 rounded">
                                        <Award className="h-3 w-3 text-green-600" />
                                    </div>
                                    <div className="ml-2">
                                        <p className="text-xs font-medium text-green-900">High Interview Success</p>
                                        <p className="text-xs text-green-700 mt-1">78% of interviews result in offers</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Performance Trends (Placeholder for Future Charts) */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-gray-900">Performance Trends</h4>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <BarChart3 className="h-4 w-4" />
                        <span>Chart visualization coming soon</span>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">18.5</div>
                        <div className="text-sm text-gray-600 mt-1">Avg Days to Fill</div>
                        <div className="text-xs text-green-600 mt-1">↓ 2.1 days</div>
                    </div>
                    
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">26.7%</div>
                        <div className="text-sm text-gray-600 mt-1">Conversion Rate</div>
                        <div className="text-xs text-green-600 mt-1">↑ 3.2%</div>
                    </div>
                    
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">4.2</div>
                        <div className="text-sm text-gray-600 mt-1">Candidate Quality Score</div>
                        <div className="text-xs text-green-600 mt-1">↑ 0.3 points</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;